// Export utility functions for CSV and PDF generation

export interface ExportableData {
  [key: string]: any;
}

// CSV Export functionality
export const exportToCSV = (data: ExportableData[], filename: string, headers?: string[]) => {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Get headers from data if not provided
  const csvHeaders = headers || Object.keys(data[0]);
  
  // Create CSV content
  const csvContent = [
    // Headers row
    csvHeaders.map(header => `"${header}"`).join(','),
    // Data rows
    ...data.map(row => 
      csvHeaders.map(header => {
        const value = row[header];
        // Handle arrays (like skills)
        if (Array.isArray(value)) {
          return `"${value.join(', ')}"`;
        }
        // Handle null/undefined values
        if (value === null || value === undefined) {
          return '""';
        }
        // Escape quotes and wrap in quotes
        return `"${String(value).replace(/"/g, '""')}"`;
      }).join(',')
    )
  ].join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// PDF Export functionality using jsPDF
export const exportToPDF = async (data: ExportableData[], filename: string, title: string, headers?: string[]) => {
  try {
    // Dynamic import to avoid bundling issues
    const { jsPDF } = await import('jspdf');
    const { autoTable } = await import('jspdf-autotable');
    
    if (!data || data.length === 0) {
      console.warn('No data to export');
      return;
    }

    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(16);
    doc.text(title, 14, 22);
    
    // Prepare data for table
    const pdfHeaders = headers || Object.keys(data[0]);
    const pdfData = data.map(row => 
      pdfHeaders.map(header => {
        const value = row[header];
        if (Array.isArray(value)) {
          return value.join(', ');
        }
        if (value === null || value === undefined) {
          return '';
        }
        return String(value);
      })
    );

    // Add table
    autoTable(doc, {
      head: [pdfHeaders],
      body: pdfData,
      startY: 30,
      styles: {
        fontSize: 8,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      margin: { top: 30 },
    });

    // Save the PDF
    doc.save(`${filename}.pdf`);
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert('Error generating PDF. Please try again.');
  }
};

// Helper function to format data for export
export const formatDataForExport = (data: any[], type: 'volunteers' | 'assignments' | 'projects' | 'tasks' | 'donations') => {
  switch (type) {
    case 'volunteers':
      return data.map(volunteer => ({
        'ID': volunteer.id,
        'Name': volunteer.name || `${volunteer.firstname || ''} ${volunteer.surname || ''}`.trim(),
        'Email': volunteer.email || '',
        'Phone': volunteer.phone || '',
        'Location': volunteer.location || '',
        'Skills': Array.isArray(volunteer.skills) ? volunteer.skills.join(', ') : '',
        'Availability': volunteer.availability || '',
        'Status': volunteer.application_status || 'pending',
        'Applied Date': volunteer.created_at ? new Date(volunteer.created_at).toLocaleDateString() : '',
        'Last Updated': volunteer.status_updated_at ? new Date(volunteer.status_updated_at).toLocaleDateString() : ''
      }));

    case 'assignments':
      return data.map(assignment => ({
        'ID': assignment.id,
        'Volunteer Name': assignment.volunteer_name || '',
        'Task Title': assignment.task_title || '',
        'Project Name': assignment.project_name || '',
        'Status': assignment.status || '',
        'Applied Date': assignment.created_at ? new Date(assignment.created_at).toLocaleDateString() : ''
      }));

    case 'projects':
      return data.map(project => ({
        'ID': project.id,
        'Name': project.name || '',
        'Description': project.description || '',
        'Status': project.status || '',
        'Created Date': project.created_at ? new Date(project.created_at).toLocaleDateString() : '',
        'Updated Date': project.updated_at ? new Date(project.updated_at).toLocaleDateString() : ''
      }));

    case 'tasks':
      return data.map(task => ({
        'ID': task.id,
        'Title': task.title || '',
        'Description': task.description || '',
        'Location': task.location || '',
        'Required Skills': Array.isArray(task.required_skills) ? task.required_skills.join(', ') : '',
        'Project ID': task.project_id || '',
        'Created Date': task.created_at ? new Date(task.created_at).toLocaleDateString() : ''
      }));

    case 'donations':
      return data.map(donation => ({
        'ID': donation.id,
        'Donor Name': `${donation.first_name || ''} ${donation.last_name || ''}`.trim(),
        'Email': donation.email || '',
        'Phone': donation.phone || '',
        'Amount': donation.amount ? `R${Number(donation.amount).toFixed(2)}` : '',
        'Type': donation.donation_type || '',
        'Transaction ID': donation.transaction_id || '',
        'Status': donation.status || 'complete',
        'Created Date': donation.created_at ? new Date(donation.created_at).toLocaleDateString() : '',
        'Updated Date': donation.updated_at ? new Date(donation.updated_at).toLocaleDateString() : ''
      }));

    default:
      return data;
  }
};

